<script setup lang="ts">
import type { ISelectItem } from "@/types"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, watch } from "vue"
import { z } from "zod"
import { useListAlbums, useListTags } from "@/api"
import { Button, ColourPicker, Combobox, Form, FormSection, Input, Select, Switch } from "@/components/ui"
import { useTranslation } from "@/composables"
import {
  SlideshowConfig,
  SlideshowConfigOrderBy,
  SlideshowConfigSortBy,
  SlideshowConfigType,
  SlideshowConfigVideoPlacement,
} from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"

interface SlideshowFormProps {
  slideshowConfig?: SlideshowConfig
  isLoading?: boolean
  tagsEnabled?: boolean
  albumsEnabled?: boolean
  slideshowVideosEnabled?: boolean
  hasFeatureImage?: boolean
}

const {
  slideshowConfig,
  isLoading = false,
  tagsEnabled = false,
  albumsEnabled = false,
  slideshowVideosEnabled = false,
  hasFeatureImage = false,
} = defineProps<SlideshowFormProps>()

const emit = defineEmits<{
  submit: [values: SlideshowConfig]
}>()

const eventStore = useEventVaultStore()

const { t } = useTranslation()

const { data: tags, isLoading: isLoadingTags } = useListTags(eventStore.eventVaultId, {
  enabled: tagsEnabled,
})

const tagSelectItems = computed<ISelectItem[]>(() => {
  if (!tagsEnabled)
    return []

  const dedupedTags = [...new Set(tags.value)]

  return dedupedTags?.map(tag => ({ label: tag, value: tag })) ?? []
})

const { data: albums, isLoading: isLoadingAlbums } = useListAlbums(eventStore.eventVaultId)

const albumSelectItems = computed<ISelectItem[]>(() => {
  if (!albumsEnabled)
    return []

  return albums.value?.map(album => ({ label: album.title ?? "", value: album.id?.toString() ?? "" })) ?? []
})

const MAX_DURATION_SECONDS = 120
const MAX_SLIDES = 1000
const MAX_TOTAL_DURATION_SECONDS = 3 * 60 * 60

function toFormSeconds(milliseconds: number) {
  return Math.round(milliseconds / 1000)
}

const validationSchema = toTypedSchema(
  z.object({
    name: z
      .string()
      .min(1, t("slideshow_form.validation.name_required"))
      .max(100, t("slideshow_form.validation.name_limit", { length: 100 })),
    intervalSeconds: z
      .number({ message: t("slideshow_form.validation.interval_number") })
      .int(t("slideshow_form.validation.interval_number"))
      .min(1, t("slideshow_form.validation.interval_min"))
      .max(MAX_DURATION_SECONDS, t("slideshow_form.validation.interval_max")),
    maxSlides: z
      .number({ message: t("slideshow_form.validation.max_slides_number") })
      .int(t("slideshow_form.validation.max_slides_number"))
      .min(1, t("slideshow_form.validation.max_slides_min"))
      .max(MAX_SLIDES, t("slideshow_form.validation.max_slides_max")),
    live: z.boolean(),
    enableDetails: z.boolean(),
    enableTitleSlide: z.boolean(),
    enableQrInTitleSlide: z.boolean(),
    titleSlideDurationSeconds: z.optional(
      z
        .number({ message: t("slideshow_form.validation.title_slide_duration_number") })
        .int(t("slideshow_form.validation.title_slide_duration_number"))
        .max(MAX_DURATION_SECONDS, t("slideshow_form.validation.title_slide_duration_max")),
    ),
    favourites: z.boolean(),
    backgroundColour: z.string(),
    videoPlacement: z.nativeEnum(SlideshowConfigVideoPlacement),
    type: z.nativeEnum(SlideshowConfigType),
    orderBy: z.nativeEnum(SlideshowConfigOrderBy),
    sortBy: z.nativeEnum(SlideshowConfigSortBy),
    tags: z.array(z.object({ label: z.string(), value: z.string() })),
    albums: z.array(z.object({ label: z.string(), value: z.string() })),
  }).superRefine((values, ctx) => {
    const titleSlideSeconds = values.enableTitleSlide ? (values.titleSlideDurationSeconds ?? 0) : 0
    const totalSeconds = values.intervalSeconds * values.maxSlides + titleSlideSeconds

    if (totalSeconds <= MAX_TOTAL_DURATION_SECONDS)
      return

    const fields = ["intervalSeconds", "maxSlides", ...(values.enableTitleSlide ? ["titleSlideDurationSeconds"] : [])]

    for (const field of fields) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field],
        message: t("slideshow_form.validation.total_duration_max"),
      })
    }
  }),
)

const {
  values: formValues,
  setValues,
  handleSubmit,
} = useForm({
  validationSchema,
  keepValuesOnUnmount: true,
  initialValues: {
    name: slideshowConfig?.name ?? "",
    intervalSeconds: toFormSeconds(slideshowConfig?.interval ?? 5000), // default of 5 seconds
    maxSlides: slideshowConfig?.maxSlides ?? 100,
    live: slideshowConfig?.live ?? false,
    enableDetails: slideshowConfig?.enableDetails ?? false,
    enableTitleSlide: slideshowConfig?.enableTitleSlide ?? false,
    enableQrInTitleSlide: slideshowConfig?.enableQrInTitleSlide ?? false,
    titleSlideDurationSeconds: toFormSeconds(slideshowConfig?.titleSlideDurationMs ?? 20000), // default of 20 seconds
    favourites: slideshowConfig?.favourites ?? false,
    backgroundColour: slideshowConfig?.backgroundColour ?? "rgb(0, 0, 0)",
    videoPlacement: slideshowConfig?.videoPlacement ?? SlideshowConfigVideoPlacement.Mixed,
    type: slideshowConfig?.type ?? SlideshowConfigType.Pics,
    orderBy: slideshowConfig?.orderBy ?? SlideshowConfigOrderBy.Desc,
    sortBy: slideshowConfig?.sortBy ?? SlideshowConfigSortBy.DateTaken,
    tags: [],
    albums: [],
  },
})

watch(
  tags,
  () => {
    if (slideshowConfig?.tags.length) {
      const selectedTagItems = tagSelectItems.value.filter(i => slideshowConfig?.tags.includes(i.value))

      setValues({ tags: selectedTagItems })
    }
  },
  { once: true },
)

watch(
  albums,
  () => {
    if (slideshowConfig?.albums.length) {
      const selectedAlbumItems = albumSelectItems.value.filter(i => slideshowConfig?.albums?.includes(Number.parseInt(i.value)))

      setValues({ albums: selectedAlbumItems })
    }
  },
  { once: true },
)

const videoPlacementOptions: ISelectItem[] = [
  {
    label: t("slideshow_form.placement_options.mixed"),
    value: SlideshowConfigVideoPlacement.Mixed,
  },
  {
    label: t("slideshow_form.placement_options.start"),
    value: SlideshowConfigVideoPlacement.Start,
  },
  {
    label: t("slideshow_form.placement_options.end"),
    value: SlideshowConfigVideoPlacement.End,
  },
]

const typeOptions: ISelectItem[] = [
  {
    label: t("slideshow_form.type_options.all"),
    value: SlideshowConfigType.All,
  },
  {
    label: t("slideshow_form.type_options.pics"),
    value: SlideshowConfigType.Pics,
  },
  {
    label: t("slideshow_form.type_options.vids"),
    value: SlideshowConfigType.Vids,
  },
]

const orderByOptions = computed<ISelectItem[]>(() => {
  const currentSort = formValues.sortBy

  const options: ISelectItem[] = []

  switch (currentSort) {
    case SlideshowConfigSortBy.Popularity:
      options.push({
        label: t("slideshow_form.order_options.least_popular"),
        value: SlideshowConfigOrderBy.Asc,
      })
      options.push({
        label: t("slideshow_form.order_options.most_popular"),
        value: SlideshowConfigOrderBy.Desc,
      })
      break
    case SlideshowConfigSortBy.Name:
      options.push({
        label: t("ascending"),
        value: SlideshowConfigOrderBy.Asc,
      })
      options.push({
        label: t("descending"),
        value: SlideshowConfigOrderBy.Desc,
      })
      break
    default:
      options.push({
        label: t("slideshow_form.order_options.oldest"),
        value: SlideshowConfigOrderBy.Asc,
      })
      options.push({
        label: t("slideshow_form.order_options.newest"),
        value: SlideshowConfigOrderBy.Desc,
      })
  }

  return options
})

const sortByOptions = computed<ISelectItem[]>(() => [
  {
    label: t("slideshow_form.sort_options.date_taken"),
    value: SlideshowConfigSortBy.DateTaken,
  },
  {
    label: t("slideshow_form.sort_options.date_uploaded"),
    value: SlideshowConfigSortBy.DateUploaded,
  },
  {
    label: t("slideshow_form.sort_options.title"),
    value: SlideshowConfigSortBy.Name,
  },
  {
    label: t("slideshow_form.sort_options.popularity"),
    value: SlideshowConfigSortBy.Popularity,
  },
  {
    label: t("slideshow_form.sort_options.random"),
    value: SlideshowConfigSortBy.Random,
  },
  {
    label: t("slideshow_form.sort_options.album_order"),
    value: SlideshowConfigSortBy.AlbumOrder,
  },
])

const isLoadingAny = computed(() => {
  return Boolean(isLoading || isLoadingAlbums.value || isLoadingTags.value)
})

const videosExcludedHint = computed(() => {
  if (!slideshowVideosEnabled || formValues.type === SlideshowConfigType.Pics)
    return undefined

  return t("slideshow_form.video_warnings.videos_excluded")
})

const liveRefreshHint = computed(() => {
  if (!slideshowVideosEnabled || !formValues.live)
    return undefined

  return t("slideshow_form.video_warnings.live_refresh")
})

const detailsHint = computed(() => {
  if (!slideshowVideosEnabled || !formValues.enableDetails)
    return undefined

  return t("slideshow_form.video_warnings.media_details_excluded")
})

const qrHint = computed(() => {
  if (!slideshowVideosEnabled || !formValues.enableTitleSlide || !formValues.enableQrInTitleSlide)
    return undefined

  return t("slideshow_form.video_warnings.qr_excluded")
})

const titleSlideNoFeatureImageHint = computed(() => {
  if (!slideshowVideosEnabled || !formValues.enableTitleSlide || hasFeatureImage)
    return undefined

  return t("slideshow_form.video_warnings.title_slide_no_feature_image")
})

const onSubmit = handleSubmit((values) => {
  const { intervalSeconds, titleSlideDurationSeconds, tags, albums, ...rest } = values

  const updatedSlideshowConfig = new SlideshowConfig({
    ...slideshowConfig,
    ...rest,
    interval: intervalSeconds * 1000,
    titleSlideDurationMs: titleSlideDurationSeconds !== undefined ? titleSlideDurationSeconds * 1000 : undefined,
    tags: tags.map(t => t.value),
    albums: albums.map(a => Number.parseInt(a.value)),
  })

  emit("submit", updatedSlideshowConfig)
})
</script>

<template>
  <div>
    <div v-show="isLoadingAny" class="flex flex-col gap-4">
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-36 h-12" />
    </div>
    <Form v-show="!isLoadingAny" @submit.prevent="onSubmit">
      <FormSection :title="t('slideshow_form.details_section_title')" :description="t('slideshow_form.details_section_description')" icon="Edit">
        <Input name="name" :label="t('slideshow_form.name_label')" />
        <Input
          name="intervalSeconds"
          type="number"
          min="1"
          :max="MAX_DURATION_SECONDS"
          step="1"
          :label="t('slideshow_form.interval_seconds_label')"
        />
        <Input
          name="maxSlides"
          type="number"
          min="1"
          :max="MAX_SLIDES"
          :label="t('slideshow_form.max_slides_label')"
        />
        <ColourPicker
          name="backgroundColour"
          :label="t('slideshow_form.background_colour_label')"
          format="rgb"
        />
        <Select
          name="type"
          :label="t('slideshow_form.type_label')"
          :items="typeOptions"
          :hint="videosExcludedHint"
        />
        <Select
          v-if="formValues.type !== SlideshowConfigType.Pics"
          name="videoPlacement"
          :label="t('slideshow_form.video_placement_label')"
          :items="videoPlacementOptions"
        />
        <Select
          name="sortBy"
          :label="t('slideshow_form.sort_label')"
          :items="sortByOptions"
        />
        <Select
          name="orderBy"
          :label="t('slideshow_form.order_label')"
          :items="orderByOptions"
          :disabled="formValues.sortBy === SlideshowConfigSortBy.Random"
        />

        <Combobox
          v-if="tagsEnabled"
          name="tags"
          :label="t('slideshow_form.tags_label')"
          multiple
          :items="tagSelectItems"
        />
        <Combobox
          v-if="albumsEnabled"
          name="albums"
          :label="t('slideshow_form.albums_label')"
          multiple
          :items="albumSelectItems"
        />
      </FormSection>

      <FormSection :title="t('slideshow_form.settings_section_title')" :description="t('slideshow_form.settings_section_description')" icon="SettingsCog">
        <div class="flex flex-col gap-2">
          <Switch
            name="live"
            variant="icon"
            :label="t('slideshow_form.live_label')"
            :hint="liveRefreshHint"
          />
          <span class="text-gray-500 text-sm">{{ t("slideshow_form.live_desc") }}</span>
        </div>

        <div class="flex flex-col gap-2">
          <Switch
            name="favourites"
            variant="icon"
            :label="t('slideshow_form.favourites_label')"
          />
          <span class="text-gray-500 text-sm">
            {{ t("slideshow_form.favourites_desc") }}
          </span>
        </div>

        <div class="flex flex-col gap-2">
          <Switch
            name="enableDetails"
            variant="icon"
            :label="t('slideshow_form.details_label')"
            :hint="detailsHint"
          />
          <span class="text-gray-500 text-sm">
            {{ t("slideshow_form.details_desc") }}
          </span>
        </div>
      </FormSection>

      <FormSection :title="t('slideshow_form.title_slide_section_title')" :description="t('slideshow_form.title_slide_section_description')" icon="Slider">
        <div class="flex flex-col gap-2">
          <Switch
            name="enableTitleSlide"
            variant="icon"
            :label="t('slideshow_form.enable_title_slide_label')"
            :hint="titleSlideNoFeatureImageHint"
          />
          <span class="text-gray-500 text-sm">
            {{ t("slideshow_form.enable_title_slide_desc") }}
          </span>
        </div>

        <div class="flex flex-col gap-2">
          <Switch
            name="enableQrInTitleSlide"
            variant="icon"
            :label="t('slideshow_form.enable_qr_label')"
            :disabled="!formValues.enableTitleSlide"
            :hint="qrHint"
          />
          <span class="text-gray-500 text-sm">{{
            t("slideshow_form.enable_qr_desc")
          }}</span>
        </div>

        <Input
          name="titleSlideDurationSeconds"
          type="number"
          min="1"
          :max="MAX_DURATION_SECONDS"
          step="1"
          :label="t('slideshow_form.title_slide_duration_seconds_label')"
          :disabled="!formValues.enableTitleSlide"
        />
      </FormSection>

      <template #actions>
        <div class="flex justify-end">
          <Button size="lg" data-test="slideshow-form-submit">
            {{ t("save") }}
          </Button>
        </div>
      </template>
    </Form>
  </div>
</template>
